import React, { useEffect, useMemo, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import AddIssue from "components/Issues/AddIssue";
import EntityAvatar from "components/Entities/EntityAvatar";
import { useGlobalSearchQuery, useGlobalSearchSuggestionsQuery, useGlobalSearchTrendingQuery, useWikipediaIssueSuggestionsInfiniteQuery } from "hooks/queries/useSearchQueries";
import LoadingSkeleton from "ui/LoadingSkeleton";
import ScrollableTabsList from "ui/ScrollableTabsList";
import { setDefaultSiteOg, setDocumentTitle } from "util/ogMetadataUtils";
import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  removeRecentSearch,
} from "util/recentSearchesUtils";
import {
  getRecentIssues,
  recentIssueToIssueEntity,
  removeRecentIssue,
  type RecentIssue,
} from "util/recentIssuesUtils";
import type { WikiSuggestion } from "util/constants";
import type {
  RecentSearchEntry,
  SearchResult,
  SearchScope,
  SearchSuggestionGroups,
  TrendingSearchGroups,
} from "types/search";
import SearchDiscoveryPanel from "./SearchDiscoveryPanel";
import SearchIssueSuggestionItem from "./SearchIssueSuggestionItem";
import SearchResultItem from "./SearchResultItem";
import SearchSectionSurface from "./SearchSectionSurface";
import SearchSuggestionsPanel from "./SearchSuggestionsPanel";
import {
  getNoResultsText,
  getResultCountText,
  getSearchCopy,
} from "./searchCopy";
import {
  buildResultPath,
  buildSearchRoute,
  buildSearchSectionRoute,
  getSearchLocale,
  isSearchScope,
} from "./searchUtils";
import { useUser } from "util/UserContext";

const SEARCH_TABS: Array<{ value: SearchScope; labelKey: "all" | "issues" | "posts" | "comments" | "people" | "tags" }> = [
  { value: "all", labelKey: "all" },
  { value: "issues", labelKey: "issues" },
  { value: "posts", labelKey: "posts" },
  { value: "comments", labelKey: "comments" },
  { value: "actors", labelKey: "people" },
  { value: "tags", labelKey: "tags" },
];

const EMPTY_SUGGESTIONS: SearchSuggestionGroups = {
  issues: [],
  posts: [],
  actors: [],
  tags: [],
};

const EMPTY_TRENDING: TrendingSearchGroups = {
  issues: [],
  posts: [],
  tags: [],
};

const normalizeIssueKey = (value: string): string => value.trim().toLowerCase();

const GlobalSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const locale = getSearchLocale(location.search);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routeQuery = searchParams.get("q")?.trim().replace(/\s+/g, " ") || "";
  const routeScope = isSearchScope(searchParams.get("type"))
    ? (searchParams.get("type") as SearchScope)
    : "all";
  const routeSection = searchParams.get("section") || "";
  const [searchInput, setSearchInput] = useState(routeQuery);
  const [debouncedInput, setDebouncedInput] = useState(routeQuery);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>([]);
  const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([]);
  const [trendingSectionLimit, setTrendingSectionLimit] = useState(12);
  const [openAddIssueModal, setOpenAddIssueModal] = useState(false);
  const [prepopulateData, setPrepopulateData] = useState<{
    name: string;
    wikipediaLink: string;
    cname: string;
    subject_properties: any;
  } | null>(null);
  const normalizedInput = searchInput.trim().replace(/\s+/g, " ");

  useEffect(() => {
    setSearchInput(routeQuery);
    setDebouncedInput(routeQuery);
    setShowSuggestions(false);
  }, [routeQuery, routeScope, routeSection]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedInput(normalizedInput);
    }, 320);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [normalizedInput]);

  useEffect(() => {
    setDocumentTitle(routeQuery ? `Search - ${routeQuery}` : "Search");
    setDefaultSiteOg({ title: document.title, url: window.location.href });
  }, [routeQuery, routeScope]);

  useEffect(() => {
    if (showSuggestions) {
      setRecentSearches(getRecentSearches());
      setRecentIssues(getRecentIssues());
    }
  }, [showSuggestions]);

  useEffect(() => {
    setRecentSearches(getRecentSearches());
    setRecentIssues(getRecentIssues());
  }, [routeSection, routeQuery]);

  const suggestionsQuery = useGlobalSearchSuggestionsQuery(
    debouncedInput,
    5,
    showSuggestions && debouncedInput.length >= 2 && debouncedInput !== routeQuery
  );
  const existingIssueCnames = useMemo(
    () =>
      (suggestionsQuery.data?.data.issues || [])
        .map((result) =>
          typeof result.metadata.cname === "string" && result.metadata.cname.trim().length > 0
            ? result.metadata.cname
            : result.title
        )
        .filter((value): value is string => typeof value === "string" && value.length > 0),
    [suggestionsQuery.data]
  );
  const wikipediaSuggestionsQuery = useWikipediaIssueSuggestionsInfiniteQuery(
    debouncedInput,
    existingIssueCnames,
    5,
    showSuggestions && debouncedInput.length >= 2 && debouncedInput !== routeQuery
  );

  const trendingQuery = useGlobalSearchTrendingQuery(
    routeSection.startsWith("trending-") ? trendingSectionLimit : 4,
    routeQuery.length === 0 && (normalizedInput.length === 0 || routeSection.startsWith("trending-"))
  );

  const resultsQuery = useGlobalSearchQuery(routeQuery, routeScope, 20, routeQuery.length >= 2);
  const resultPages = resultsQuery.data?.pages || [];
  const results = resultPages.flatMap((page) => page.data);
  const latestPagination = resultPages.length > 0 ? resultPages[resultPages.length - 1].pagination : null;
  const resultIssueKeys = useMemo(
    () =>
      results
        .filter((result) => result.type === "issue")
        .map((result) =>
          typeof result.metadata.cname === "string" && result.metadata.cname.trim().length > 0
            ? result.metadata.cname
            : result.title
        )
        .filter((value): value is string => typeof value === "string" && value.length > 0),
    [results]
  );
  const wikipediaResultsQuery = useWikipediaIssueSuggestionsInfiniteQuery(
    routeQuery,
    resultIssueKeys,
    routeScope === "issues" ? 10 : 6,
    (routeScope === "all" || routeScope === "issues") && routeQuery.length >= 2
  );

  const suggestionGroups = suggestionsQuery.data?.data || EMPTY_SUGGESTIONS;
  const wikipediaSuggestions = useMemo(() => {
    const existingIssueSet = new Set(existingIssueCnames.map(normalizeIssueKey));
    const seenWikipedia = new Set<string>();
    const rawSuggestions = wikipediaSuggestionsQuery.data?.pages.flatMap((page) => page.items) || [];

    return rawSuggestions.filter((suggestion) => {
      const suggestionKey = normalizeIssueKey(suggestion.cname || suggestion.name);

      if (existingIssueSet.has(suggestionKey) || seenWikipedia.has(suggestionKey)) {
        return false;
      }

      seenWikipedia.add(suggestionKey);
      return true;
    }) as WikiSuggestion[];
  }, [existingIssueCnames, wikipediaSuggestionsQuery.data]);
  const wikipediaResults = useMemo(() => {
    const existingIssueSet = new Set(resultIssueKeys.map(normalizeIssueKey));
    const seenWikipedia = new Set<string>();
    const rawSuggestions = wikipediaResultsQuery.data?.pages.flatMap((page) => page.items) || [];

    return rawSuggestions.filter((suggestion) => {
      const suggestionKey = normalizeIssueKey(suggestion.cname || suggestion.name);

      if (existingIssueSet.has(suggestionKey) || seenWikipedia.has(suggestionKey)) {
        return false;
      }

      seenWikipedia.add(suggestionKey);
      return true;
    }) as WikiSuggestion[];
  }, [resultIssueKeys, wikipediaResultsQuery.data]);
  const trendingGroups = trendingQuery.data?.data || EMPTY_TRENDING;
  const hasSuggestionResults =
    suggestionGroups.issues.length > 0 ||
    wikipediaSuggestions.length > 0 ||
    suggestionGroups.posts.length > 0 ||
    suggestionGroups.actors.length > 0 ||
    suggestionGroups.tags.length > 0;

  const handleOpenSearchRoute = (query: string, scope: SearchScope) => {
    const normalizedQuery = query.trim().replace(/\s+/g, " ");

    if (normalizedQuery.length < 2) {
      return;
    }

    addRecentSearch(normalizedQuery, scope);
    navigate(buildSearchRoute(normalizedQuery, scope, locale));
  };

  const handleSelectResult = (result: SearchResult) => {
    if (routeQuery.length >= 2) {
      addRecentSearch(routeQuery, routeScope);
    } else if (normalizedInput.length >= 2) {
      addRecentSearch(normalizedInput, "all");
    }

    navigate(buildResultPath(result, locale));
  };
  const handleSelectIssueSuggestion = (suggestion: WikiSuggestion) => {
    if (awaitingUser) {
      return;
    }

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setPrepopulateData({
      name: suggestion.name,
      wikipediaLink: suggestion.link,
      cname: suggestion.cname,
      subject_properties: suggestion.subject_properties,
    });
    setOpenAddIssueModal(true);
    setShowSuggestions(false);
  };
  const searchHomeRoute = `/search?locale=${locale}`;

  const renderSectionSurface = () => {
    if (routeSection === "recent-searches") {
      return (
        <SearchSectionSurface
          title={getSearchCopy(locale, "recentSearches")}
          locale={locale}
          onBack={() => navigate(searchHomeRoute)}
          headerAction={
            recentSearches.length > 0 ? (
              <button
                type="button"
                className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub hover:text-eoe-text dark:hover:text-eoe-dark-text"
                onClick={() => {
                  clearRecentSearches();
                  setRecentSearches([]);
                }}
              >
                {getSearchCopy(locale, "clearRecent")}
              </button>
            ) : null
          }
        >
          <ul className="">
            {recentSearches.length > 0 ? (
              recentSearches.map((entry) => (
                <li
                  key={`surface-recent-search-${entry.id}`}
                  className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text flex justify-between items-center"
                >
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left"
                    onClick={() => navigate(buildSearchRoute(entry.query, entry.scope, locale))}
                  >
                    <span className="w-8 h-8 flex-shrink-0 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon flex items-center justify-center">
                      <Clock className="size-4" />
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">{entry.query}</span>
                      <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {getSearchCopy(
                          locale,
                          entry.scope === "actors" ? "people" : entry.scope
                        )}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                    onClick={() => {
                      removeRecentSearch(entry.id);
                      setRecentSearches(getRecentSearches());
                    }}
                    aria-label={getSearchCopy(locale, "removeRecent")}
                  >
                    ×
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-3 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {getSearchCopy(locale, "recentSearches")}
              </li>
            )}
          </ul>
        </SearchSectionSurface>
      );
    }

    if (routeSection === "recent-issues") {
      return (
        <SearchSectionSurface
          title={getSearchCopy(locale, "recentIssues")}
          locale={locale}
          onBack={() => navigate(searchHomeRoute)}
        >
          <ul className="">
            {recentIssues.map((recentIssue) => (
              <li
                key={`surface-recent-issue-${recentIssue.id}`}
                className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text"
                onClick={() => navigate(`/issue/${recentIssue.cname}?locale=${locale}`)}
              >
                <EntityAvatar
                  entity={recentIssueToIssueEntity(recentIssue)}
                  entityType="issue"
                  entityId={recentIssue.id}
                  entityChip={true}
                  showCloseChip={true}
                  onEntityChipClose={() => {
                    removeRecentIssue(recentIssue.id);
                    setRecentIssues(getRecentIssues());
                  }}
                  showLink={false}
                  className="text-xs pr-0"
                />
              </li>
            ))}
          </ul>
        </SearchSectionSurface>
      );
    }

    if (
      routeSection === "trending-issues" ||
      routeSection === "trending-posts" ||
      routeSection === "trending-tags"
    ) {
      const sectionKey =
        routeSection === "trending-issues"
          ? "issues"
          : routeSection === "trending-posts"
            ? "posts"
            : "tags";
      const sectionItems = trendingGroups[sectionKey];

      return (
        <SearchSectionSurface
          title={getSearchCopy(locale, sectionKey === "issues" ? "issues" : sectionKey === "posts" ? "posts" : "tags")}
          locale={locale}
          onBack={() => navigate(searchHomeRoute)}
          onScrollEnd={() => {
            if (sectionItems.length >= trendingSectionLimit && trendingSectionLimit < 60) {
              setTrendingSectionLimit((currentValue) => currentValue + 12);
            }
          }}
          footer={
            sectionItems.length >= trendingSectionLimit && trendingSectionLimit < 60 ? (
              <button
                type="button"
                className="w-full font-semibold text-sm text-action-text dark:text-action-text hover:text-action-text-hover dark:hover:text-action-text-hover disabled:opacity-60"
                onClick={() => setTrendingSectionLimit((currentValue) => currentValue + 12)}
                disabled={trendingQuery.isFetching}
                aria-busy={trendingQuery.isFetching}
                aria-label={
                  trendingQuery.isFetching
                    ? getSearchCopy(locale, "loading")
                    : getSearchCopy(locale, "loadMore")
                }
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {trendingQuery.isFetching ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    getSearchCopy(locale, "loadMore")
                  )}
                </span>
              </button>
            ) : null
          }
        >
          <ul className="">
            {sectionItems.map((result) => (
              <SearchResultItem
                key={result.id}
                result={result}
                locale={locale}
                onSelect={handleSelectResult}
              />
            ))}
          </ul>
        </SearchSectionSurface>
      );
    }

    return null;
  };

  return (
    <div className="section-page">
      <div className="max-w-5xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleOpenSearchRoute(searchInput, routeScope);
                }
                if (event.key === "Escape") {
                  setShowSuggestions(false);
                }
              }}
              placeholder={getSearchCopy(locale, "searchPlaceholder")}
              className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-3 py-3 pr-10"
            />
            {searchInput.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setDebouncedInput("");
                  setShowSuggestions(true);
                  if (routeQuery) {
                    navigate(buildSearchRoute("", "all", locale));
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                aria-label={getSearchCopy(locale, "closeSearch")}
              >
                ×
              </button>
            ) : null}
          </div>

          {showSuggestions ? (
            <div className="search-suggestions-container flex flex-col bg-transparent relative top-1 z-30">
              <ul className="border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 shadow-lg overflow-y-auto max-h-[calc(100dvh-180px)]">
                {debouncedInput.length === 0 ? (
                  <SearchDiscoveryPanel
                    locale={locale}
                    recentSearches={recentSearches}
                    recentIssues={recentIssues}
                    trendingGroups={trendingGroups}
                    onRemoveRecentSearch={(entryId) => {
                      removeRecentSearch(entryId);
                      setRecentSearches(getRecentSearches());
                    }}
                    onRemoveRecentIssue={(issueId) => {
                      removeRecentIssue(issueId);
                      setRecentIssues(getRecentIssues());
                    }}
                    onSelectRecentSearch={(entry) => {
                      navigate(buildSearchRoute(entry.query, entry.scope, locale));
                      setShowSuggestions(false);
                    }}
                    onSeeAllRecentSearches={() => {
                      navigate(buildSearchSectionRoute("recent-searches", locale));
                      setShowSuggestions(false);
                    }}
                    onSelectRecentIssue={(recentIssue) => {
                      navigate(`/issue/${recentIssue.cname}?locale=${locale}`);
                      setShowSuggestions(false);
                    }}
                    onSeeAllRecentIssues={() => {
                      navigate(buildSearchSectionRoute("recent-issues", locale));
                      setShowSuggestions(false);
                    }}
                    onSeeAllTrendingSection={(section) => {
                      navigate(buildSearchSectionRoute(`trending-${section}`, locale));
                      setShowSuggestions(false);
                    }}
                    onSelectTrendingResult={handleSelectResult}
                  />
                ) : suggestionsQuery.isLoading || wikipediaSuggestionsQuery.isLoading ? (
                  <li className="px-2 py-2">
                    <LoadingSkeleton
                      avatarSize="h-9 w-9"
                      variant="entity-avatar"
                      count={5}
                    />
                  </li>
                ) : hasSuggestionResults ? (
                  <SearchSuggestionsPanel
                    groups={suggestionGroups}
                    wikipediaSuggestions={wikipediaSuggestions}
                    locale={locale}
                    onSeeAllSection={(scope) => handleOpenSearchRoute(searchInput, scope)}
                    onSelectIssueSuggestion={handleSelectIssueSuggestion}
                    onSelectResult={(result) => {
                      handleSelectResult(result);
                      setShowSuggestions(false);
                    }}
                  />
                ) : (
                  <li className="px-3 py-3 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
                      {getNoResultsText(locale, debouncedInput)}
                    </div>
                    <div className="mt-1">
                      {getSearchCopy(locale, "noResultsHint")}
                    </div>
                  </li>
                )}
              </ul>
              {wikipediaSuggestionsQuery.isFetchingNextPage ? (
                <div
                  className="flex items-center justify-center gap-2 px-2 py-3 border border-eoe-border-main dark:border-eoe-dark-border-main border-t-0 shadow-lg text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2
                    className="size-4 animate-spin"
                    aria-label={getSearchCopy(locale, "loading")}
                  />
                  <span>{getSearchCopy(locale, "loadingSuggestions")}</span>
                </div>
              ) : null}
              <div className="flex-shrink-0 border-none">
                <button
                  className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none"
                  onClick={() => setShowSuggestions(false)}
                >
                  <span className="text-eoe-dark-text">
                    {getSearchCopy(locale, "closeSearch")}
                  </span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="py-2">
          <ScrollableTabsList
            navlink={false}
            tabs={SEARCH_TABS.map((tab) => ({
              label: getSearchCopy(locale, tab.labelKey),
              value: tab.value,
            }))}
            selectedTab={routeScope}
            onTabChange={(newTab) => handleOpenSearchRoute(routeQuery || searchInput, newTab as SearchScope)}
            pageTitle={getSearchCopy(locale, "searchTitle")}
          />
        </div>

        {routeQuery.length < 2 && routeSection ? (
          renderSectionSurface()
        ) : routeQuery.length < 2 ? (
          <SearchSectionSurface
            title={getSearchCopy(locale, "searchTitle")}
            locale={locale}
            onBack={() => navigate("/")}
          >
            <ul className="">
              <SearchDiscoveryPanel
                locale={locale}
                recentSearches={recentSearches.slice(0, 6)}
                recentIssues={recentIssues.slice(0, 6)}
                trendingGroups={trendingGroups}
                onRemoveRecentSearch={(entryId) => {
                  removeRecentSearch(entryId);
                  setRecentSearches(getRecentSearches());
                }}
                onRemoveRecentIssue={(issueId) => {
                  removeRecentIssue(issueId);
                  setRecentIssues(getRecentIssues());
                }}
                onSelectRecentSearch={(entry) => navigate(buildSearchRoute(entry.query, entry.scope, locale))}
                onSeeAllRecentSearches={() => navigate(buildSearchSectionRoute("recent-searches", locale))}
                onSelectRecentIssue={(recentIssue) => navigate(`/issue/${recentIssue.cname}?locale=${locale}`)}
                onSeeAllRecentIssues={() => navigate(buildSearchSectionRoute("recent-issues", locale))}
                onSeeAllTrendingSection={(section) => navigate(buildSearchSectionRoute(`trending-${section}`, locale))}
                onSelectTrendingResult={handleSelectResult}
              />
            </ul>
          </SearchSectionSurface>
        ) : resultsQuery.isLoading ? (
          <LoadingSkeleton
            avatarSize="h-9 w-9"
            variant="entity-avatar"
            count={10}
          />
        ) : resultsQuery.isError ? (
          <div className="card-main-profile-page p-6">
            <p className="text-warning-red dark:text-dark-warning-red">
              {getSearchCopy(locale, "searchError")}
            </p>
          </div>
        ) : results.length === 0 && wikipediaResults.length === 0 ? (
          <div className="card-main-profile-page p-6">
            <p className="font-semibold text-eoe-text dark:text-eoe-dark-text">
              {getNoResultsText(locale, routeQuery)}
            </p>
            <p className="mt-2 card-description">
              {getSearchCopy(locale, "noResultsHint")}
            </p>
          </div>
        ) : (
          <>
            <div className="px-1 pb-2 text-sm text-eoe-text-meta dark:text-eoe-dark-text-meta">
              {getResultCountText(locale, latestPagination?.total || results.length)}
            </div>
            <div className="border border-eoe-border-main dark:border-eoe-dark-border-main rounded-md overflow-hidden">
              {results.map((result) => (
                <SearchResultItem
                  key={result.id}
                  result={result}
                  locale={locale}
                  onSelect={handleSelectResult}
                />
              ))}
              {(routeScope === "all" || routeScope === "issues") && wikipediaResults.length > 0 ? (
                <>
                  <div className="px-4 py-1 text-left border-t border-eoe-border-main dark:border-eoe-dark-border-main">
                    <span className="text-[11px] text-eoe-text-meta dark:text-eoe-dark-text-meta uppercase">
                      Wikipedia
                    </span>
                  </div>
                  {wikipediaResults.map((suggestion, index) => (
                    <SearchIssueSuggestionItem
                      key={`${suggestion.cname}-${index}`}
                      suggestion={suggestion}
                      onSelect={handleSelectIssueSuggestion}
                    />
                  ))}
                </>
              ) : null}
            </div>
            {resultsQuery.hasNextPage || wikipediaResultsQuery.hasNextPage ? (
              <div className="flex justify-center py-2">
                {(() => {
                  const isLoadingMore =
                    resultsQuery.isFetchingNextPage ||
                    wikipediaResultsQuery.isFetchingNextPage;
                  return (
                    <button
                      type="button"
                      className="font-semibold text-sm text-action-text dark:text-action-text hover:text-action-text-hover dark:hover:text-action-text-hover disabled:opacity-60 inline-flex items-center justify-center gap-2"
                      onClick={async () => {
                        const actions: Array<Promise<unknown>> = [];

                        if (resultsQuery.hasNextPage) {
                          actions.push(resultsQuery.fetchNextPage());
                        }

                        if (
                          (routeScope === "all" || routeScope === "issues") &&
                          wikipediaResultsQuery.hasNextPage
                        ) {
                          actions.push(wikipediaResultsQuery.fetchNextPage());
                        }

                        await Promise.all(actions);
                      }}
                      disabled={isLoadingMore}
                      aria-busy={isLoadingMore}
                      aria-label={
                        isLoadingMore
                          ? getSearchCopy(locale, "loading")
                          : getSearchCopy(locale, "loadMore")
                      }
                    >
                      {isLoadingMore ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        getSearchCopy(locale, "loadMore")
                      )}
                    </button>
                  );
                })()}
              </div>
            ) : null}
          </>
        )}

        {openAddIssueModal && (
          <AddIssue
            open={openAddIssueModal}
            onClose={() => setOpenAddIssueModal(false)}
            prepopulateData={prepopulateData}
          />
        )}
      </div>
    </div>
  );
};

export default GlobalSearchPage;
